<template>
  <ModalComp
    :is-visible="modalVisible"
    title="Fiche acheteur"
    size="lg"
    hide-footer
    @update:is-visible="onModalVisibilityChange"
  >
    <template #body>
      <div v-if="detailsLoading" class="py-10 text-center text-gray-500">
        <i class="fas fa-spinner fa-spin text-2xl mb-3"></i>
        <p>Chargement…</p>
      </div>

      <div v-else-if="detailsError" class="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
        {{ detailsError }}
      </div>

      <div v-else-if="details" class="space-y-4 text-sm">
        <section class="rounded-lg border border-indigo-200 bg-indigo-50 p-3">
          <h4 class="font-semibold text-indigo-900 mb-2">Activité commandes</h4>
          <dl class="grid grid-cols-2 gap-x-4 gap-y-1 text-gray-700">
            <dt class="text-gray-500">Nombre de commandes</dt>
            <dd class="font-medium">{{ details.nbCommandes }}</dd>
            <dt class="text-gray-500">Dernière commande</dt>
            <dd>{{ details.derniereCommande ? formatDate(details.derniereCommande) : '—' }}</dd>
            <dt class="text-gray-500">CA total</dt>
            <dd class="font-medium">{{ formatPrice(details.caTotal) }}</dd>
          </dl>
          <div class="mt-3 flex flex-wrap gap-2">
            <RouterLink
              :to="{
                name: 'adminEFP/orders',
                query: { acheteur_id: String(details.id_acheteur) },
              }"
              class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
              @click="modalVisible = false"
            >
              <i class="fas fa-shopping-cart"></i>
              Voir ses commandes
            </RouterLink>
            <button
              type="button"
              :disabled="customerUrlLoading"
              class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-indigo-200 bg-white text-indigo-700 hover:bg-indigo-50 transition-colors disabled:opacity-50"
              @click="openCustomerPage(details.id_acheteur)"
            >
              <i :class="customerUrlLoading ? 'fas fa-spinner fa-spin' : 'fas fa-external-link-alt'"></i>
              Voir la page client
            </button>
          </div>
        </section>

        <section>
          <h4 class="font-semibold text-gray-900 mb-2">Identité</h4>
          <dl class="grid grid-cols-2 gap-x-4 gap-y-1 text-gray-700">
            <dt class="text-gray-500">ID acheteur</dt>
            <dd class="font-mono text-xs">{{ details.id_acheteur }}</dd>
            <dt class="text-gray-500">Société</dt>
            <dd>{{ details.nomSociete || '—' }}</dd>
            <dt class="text-gray-500">Contact</dt>
            <dd>
              {{ [details.prenomContact, details.nomContact].filter(Boolean).join(' ') || '—' }}
            </dd>
            <dt class="text-gray-500">Email</dt>
            <dd class="break-all">{{ details.email || '—' }}</dd>
            <dt class="text-gray-500">Statut eFashion</dt>
            <dd>
              <span
                :class="
                  details.statutEfashion === 'Actif' ||
                  details.statutEfashion === 'Validé'
                    ? 'text-green-700 font-medium'
                    : 'text-amber-700 font-medium'
                "
              >
                {{ details.statutEfashion }}
              </span>
            </dd>
            <dt class="text-gray-500">Statut compte</dt>
            <dd>
              <span
                :class="details.statut === 1 ? 'text-green-700' : 'text-amber-700'"
                class="font-medium"
              >
                {{ details.statutCompte }}
              </span>
              <span
                v-if="details.in_tmp"
                class="ml-2 text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded-full"
              >
                Nouveau compte — validation en attente
              </span>
            </dd>
            <dt class="text-gray-500">Date création</dt>
            <dd>{{ formatDate(details.dateCreation) }}</dd>
            <dt class="text-gray-500">Téléphone fixe</dt>
            <dd>
              <a
                v-if="details.telephone"
                :href="`tel:${details.telephone}`"
                class="text-indigo-600 hover:underline"
              >{{ details.telephone }}</a>
              <span v-else>—</span>
            </dd>
            <dt class="text-gray-500">Mobile</dt>
            <dd>
              <a
                v-if="details.mobile"
                :href="`tel:${details.mobile}`"
                class="text-indigo-600 hover:underline"
              >{{ details.mobile }}</a>
              <span v-else>—</span>
            </dd>
          </dl>
        </section>

        <section v-if="details.adresseLivraison || details.adresseFacturation">
          <h4 class="font-semibold text-gray-900 mb-2">Adresses</h4>
          <div class="space-y-3">
            <div
              v-if="details.adresseLivraison"
              class="rounded-lg border border-gray-200 p-3 bg-gray-50"
            >
              <p class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                Livraison
                <span v-if="details.adresseLivraison.typeLibelle" class="normal-case">
                  — {{ details.adresseLivraison.typeLibelle }}
                </span>
              </p>
              <p class="text-gray-800 whitespace-pre-line">{{ formatAdresseBlock(details.adresseLivraison) }}</p>
            </div>
            <div
              v-if="details.adresseFacturation"
              class="rounded-lg border border-gray-200 p-3 bg-gray-50"
            >
              <p class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                Facturation
                <span v-if="details.adresseFacturation.typeLibelle" class="normal-case">
                  — {{ details.adresseFacturation.typeLibelle }}
                </span>
              </p>
              <p class="text-gray-800 whitespace-pre-line">{{ formatAdresseBlock(details.adresseFacturation) }}</p>
            </div>
          </div>
        </section>

        <section>
          <h4 class="font-semibold text-gray-900 mb-2">Fiscal &amp; international</h4>
          <dl class="grid grid-cols-2 gap-x-4 gap-y-1 text-gray-700">
            <dt class="text-gray-500">Régime TVA</dt>
            <dd :class="details.active_tva === 1 ? 'text-green-700' : 'text-amber-700'">
              {{ details.regimeTva }}
            </dd>
            <dt class="text-gray-500">SIRET / TVA intra</dt>
            <dd class="flex items-center flex-wrap gap-2 break-all">
              <span>{{ details.tva_intra || '—' }}</span>
              <a
                v-if="details.tva_intra"
                href="https://ec.europa.eu/taxation_customs/vies/"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 hover:underline"
              >
                <i class="fas fa-external-link-alt"></i>
                VIES
              </a>
            </dd>
            <dt class="text-gray-500">EORI</dt>
            <dd class="break-all">{{ details.eori || '—' }}</dd>
            <dt class="text-gray-500">Pays</dt>
            <dd>{{ details.pays || '—' }}</dd>
            <dt class="text-gray-500">Langue</dt>
            <dd>{{ details.LangueDefaut || '—' }}</dd>
          </dl>
        </section>

        <section
          v-if="details.vendeursBloqueurs.length"
          class="rounded-lg border border-red-200 bg-red-50 p-3"
        >
          <h4 class="font-semibold text-red-900 mb-2">
            <i class="fas fa-ban mr-1 text-red-500"></i>
            Bloqué par {{ details.vendeursBloqueurs.length }} vendeur(s)
          </h4>
          <ul class="space-y-2">
            <li
              v-for="v in details.vendeursBloqueurs"
              :key="v.id_vendeur"
              class="text-sm text-red-900 flex flex-wrap items-baseline gap-x-2"
            >
              <span class="font-medium">{{ v.vendeurLabel }}</span>
              <span class="text-xs text-red-700 font-mono">#{{ v.id_vendeur }}</span>
              <span v-if="v.dateDisabled" class="text-xs text-red-600">
                · {{ formatDate(v.dateDisabled) }}
              </span>
            </li>
          </ul>
        </section>

        <section v-if="details.site_web || details.commentaires">
          <h4 class="font-semibold text-gray-900 mb-2">Compléments</h4>
          <dl class="grid grid-cols-2 gap-x-4 gap-y-1 text-gray-700">
            <template v-if="details.site_web">
              <dt class="text-gray-500">Site web</dt>
              <dd class="break-all">
                <a
                  :href="formatSiteUrl(details.site_web)"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-indigo-600 hover:underline"
                >
                  {{ details.site_web }}
                </a>
              </dd>
            </template>
            <template v-if="details.commentaires">
              <dt class="text-gray-500">Commentaires</dt>
              <dd class="col-span-1 whitespace-pre-wrap break-words">{{ details.commentaires }}</dd>
            </template>
          </dl>
        </section>

        <AdminBuyerComments :id-acheteur="Number(details.id_acheteur)" />

        <div class="flex justify-end pt-2">
          <button
            type="button"
            class="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
            @click="modalVisible = false"
          >
            Fermer
          </button>
        </div>
      </div>
    </template>
  </ModalComp>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { ModalComp } from '@/components'
import AdminBuyerComments from '@/components/order/AdminBuyerComments.vue'
import { useAdminEfpStore } from '@/stores/adminEfp'
import type { AdminEfpAcheteurAdresse, AdminEfpAcheteurDetails } from '@/api/adminEfp'

const adminEfp = useAdminEfpStore()

const modalVisible = ref(false)
const details = ref<AdminEfpAcheteurDetails | null>(null)
const detailsLoading = ref(false)
const detailsError = ref('')
const customerUrlLoading = ref(false)

async function open(idAcheteur: number) {
  modalVisible.value = true
  details.value = null
  detailsError.value = ''
  detailsLoading.value = true
  try {
    details.value = await adminEfp.getAcheteurDetails(idAcheteur)
  } catch (e) {
    detailsError.value = e instanceof Error ? e.message : 'Erreur de chargement'
  } finally {
    detailsLoading.value = false
  }
}

function onModalVisibilityChange(visible: boolean) {
  modalVisible.value = visible
}

function formatDate(d: string | null | undefined): string {
  if (!d) return '—'
  const dt = new Date(d)
  if (Number.isNaN(dt.getTime())) return String(d)
  return new Intl.DateTimeFormat('fr-FR', { dateStyle: 'medium' }).format(dt)
}

function formatSiteUrl(url: string): string {
  if (/^https?:\/\//i.test(url)) return url
  return `https://${url}`
}

function formatPrice(n: number): string {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n)
}

async function openCustomerPage(id: number | string) {
  if (customerUrlLoading.value) return
  customerUrlLoading.value = true
  const win = window.open('', '_blank')
  try {
    const url = await adminEfp.getCustomerAuthUrl(Number(id))
    if (win) {
      win.opener = null
      win.location.href = url
    } else {
      window.open(url, '_blank', 'noopener,noreferrer')
    }
  } catch (e) {
    if (win) win.close()
    detailsError.value =
      e instanceof Error ? e.message : 'Erreur ouverture page client'
  } finally {
    customerUrlLoading.value = false
  }
}

function formatAdresseBlock(adr: AdminEfpAcheteurAdresse): string {
  const lines: string[] = []
  if (adr.societe) lines.push(adr.societe)
  if (adr.contact) lines.push(adr.contact)
  if (adr.ligneAdresse) lines.push(adr.ligneAdresse)
  const cpVille = [adr.codePostal, adr.ville].filter(Boolean).join(' ')
  if (cpVille) lines.push(cpVille)
  if (adr.pays) lines.push(adr.pays)
  const phones = [adr.telephone, adr.mobile].filter(Boolean)
  if (phones.length) lines.push(phones.join(' · '))
  return lines.length ? lines.join('\n') : '—'
}

watch(modalVisible, (visible) => {
  if (!visible) {
    details.value = null
    detailsError.value = ''
  }
})

defineExpose({ open })
</script>
